/**
 * Converts a hex color to RGB.
 */
function hexToRgb(hex: string): [number, number, number] {
  const cleanHex = hex.replace("#", "")
  const bigint = parseInt(cleanHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return [r, g, b]
}

/**
 * Calculates relative luminance as per WCAG formula.
 */
function luminance(r: number, g: number, b: number): number {
  const srgb = [r, g, b].map((val) => {
    const s = val / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2]
}

/**
 * Calculates contrast ratio between two luminance values.
 */
function contrastRatio(lum1: number, lum2: number): number {
  const [L1, L2] = [lum1, lum2].sort((a, b) => b - a)
  return (L1 + 0.05) / (L2 + 0.05)
}

/**
 * Main function to check WCAG compliance.
 * @param foreground - Hex color string (e.g., "#ffffff")
 * @param background - Hex color string (e.g., "#000000")
 * @returns Object with compliance results
 */
export function isWCAGCompliant(foreground: string, background: string) {
  const fgRgb = hexToRgb(foreground)
  const bgRgb = hexToRgb(background)
  const fgLum = luminance(...fgRgb)
  const bgLum = luminance(...bgRgb)
  const ratio = contrastRatio(fgLum, bgLum)

  return {
    ratio: ratio,
    AA_normal_text: ratio >= 4.5,
    AA_large_text: ratio >= 3,
    AAA_normal_text: ratio >= 7,
    AAA_large_text: ratio >= 4.5,
  }
}
